import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

/**
 * Reveal — animates children in on viewport enter.
 */
export function Reveal({
  as: Tag = "div",
  children,
  className = "",
  delay = 0,
  y = 24,
  once = true,
  amount = 0.25,
  ...rest
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount });
  const reduced = useReducedMotion();
  const MotionTag = motion[Tag] || motion.div;

  return (
    <MotionTag
      ref={ref}
      initial={reduced ? false : { opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * LineReveal — split a headline into lines; each line is masked (overflow-hidden)
 * and slides up on load. Pass an array of strings OR pass children preformatted.
 */
export function LineReveal({ lines = [], className = "", lineClassName = "", delay = 0, stagger = 0.08 }) {
  const reduced = useReducedMotion();
  return (
    <span className={className} aria-label={lines.join(" ")}>
      {lines.map((l, i) => (
        <span
          key={i}
          className="block overflow-hidden pb-[0.08em]"
          aria-hidden="true"
        >
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={reduced ? false : { y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 0.95,
              delay: delay + i * stagger,
              ease: [0.22, 1, 0.36, 1],
            }}
            dangerouslySetInnerHTML={{ __html: l }}
          />
        </span>
      ))}
    </span>
  );
}

/**
 * WordReveal — split a phrase into words, each rising with tiny stagger.
 */
export function WordReveal({ text, className = "", delay = 0, stagger = 0.03 }) {
  const reduced = useReducedMotion();
  const words = text.split(" ");
  return (
    <span className={`${className}`} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-baseline">
          <motion.span
            className="inline-block will-change-transform"
            initial={reduced ? false : { y: "100%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 0.7, delay: delay + i * stagger, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </span>
  );
}
